import React, { useEffect } from "react";
import { useState } from "react";
import { useAppSelector } from "../../store/hooks";
import { NavItemComponent as NavItemComponent_ } from "./interfaces/base";
import { Link, useLocation } from "react-router-dom";
import RenderChildren from "./RenderChildren";
import styled from "@emotion/styled";

import MegaMenuComponent from "./MegaMenuComponent";
import { EditorContent } from "@tiptap/react";
import { getPageLocaleUrl } from "./utilities/helpers";
import {
  appendForwardedUrlParams,
  resolveBuilderLinkHref,
} from "./utilities/linkHref";
import {
  getComponentFromStructureMap,
  getRelationChildKey,
  matchesEditorTarget,
} from "./utilities/componentIdentity";
import "./Nav.css";

const Li = styled("li")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

interface NavItemComponentProps {
  id: string | number;
  childElements?: any;
  isMobile?: boolean;
  mobileMenuOpen?: boolean;
  setMobileMenuOpen?: (open: boolean) => void;
}

export default function NavItemComponent({
  id,
  childElements,
  isMobile,
  mobileMenuOpen,
  setMobileMenuOpen,
}: NavItemComponentProps) {
  const component = useAppSelector(
    (state) =>
      getComponentFromStructureMap(
        state.builder.structure.components,
        id
      ) as NavItemComponent_
  );

  const isBuilder = document.getElementById("BuilderContent");
  let editor = useAppSelector((state) => state.builder?.editor?.editor);

  const attributes = component?.attributes;

  const childWebpageComponentRelations =
    component?.childWebpageComponentRelations;

  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const isEditing = matchesEditorTarget(component, editorId);

  const [styles, setStyle] = useState<React.CSSProperties>();
  const typographies = useAppSelector((state) => state.builder.typographies);

  const colors = useAppSelector((state) => state.builder.colors);
  const pages = useAppSelector((state) => state.builder.pages);
  const structure = useAppSelector((state) => state.builder.structure);

  const languages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );

  const compTypography = typographies?.find(
    (x: any) => x.id === component?.attributes?.typography_id
  );

  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (compTypography) {
      setStyle({
        ...styles,
        ...{
          fontSize: compTypography.fontSize + compTypography.fontSizeUnit,
          fontFamily: compTypography.fontFamily,
          fontStyle: compTypography.fontStyle,
          fontWeight: compTypography.fontWeight,
          letterSpacing: compTypography.letterSpacing,
          lineHeight: compTypography.lineHeight,
          color: colors?.find((x: any) => x.id === compTypography.colorId)
            ?.color,
        },
      });
    }
  }, [typographies, attributes]);

  function generateLink(link: any, shouldForwardUrlParams = false) {
    let href = resolveBuilderLinkHref(link, pages.list, (page) =>
      getPageLocaleUrl(page, languages)
    );
    if (shouldForwardUrlParams) {
      href = appendForwardedUrlParams(
        href,
        link?.type,
        location.search,
        location.pathname
      );
    }

    return link.type === "Page" ? (
      <Link
        className="nav-link"
        key={link.text + link.type + href}
        to={href || "#"} // Avoid redirecting when editing
        target={
          link?.type === "Web" ||
          link?.type === "None" ||
          link?.type === "Anchor" ||
          link?.type === "Phone" ||
          link?.type === "Email"
            ? "_self"
            : link?.target
        }
        onClick={() => {
          if (isMobile && mobileMenuOpen && setMobileMenuOpen) {
            setMobileMenuOpen(false);
          }
        }}
        contentEditable={isEditing}
        dangerouslySetInnerHTML={{ __html: link?.text }}
        style={{
          ...styles,
          pointerEvents: "auto",
        }}
      ></Link>
    ) : (
      <a
        className="nav-link"
        key={link.text + link.type + href}
        href={href}
        target={
          link?.type === "Web" ||
          link?.type === "None" ||
          link?.type === "Anchor" ||
          link?.type === "Phone" ||
          link?.type === "Email"
            ? "_self"
            : link?.target
        }
        contentEditable={isEditing}
        dangerouslySetInnerHTML={{ __html: link?.text }}
        onClick={(e) => {
          if (isBuilder) {
            e.preventDefault();
          }
          if (isMobile && mobileMenuOpen && setMobileMenuOpen) {
            setMobileMenuOpen(false);
          }
        }}
        style={{
          ...styles,
          zIndex: 2,
          pointerEvents: "auto",
        }}
      ></a>
    );
  }

  if (
    !attributes ||
    !attributes.link ||
    attributes === undefined ||
    !component
  ) {
    return <></>;
  }

  const classes = attributes ? attributes.classes : {};

  const handleMegaMenuOpen = (value: boolean) => {
    if (attributes.link.type == "MegaMenu") {
      setMegaMenuOpen(value);
    }
  };

  // If dropdown use div instead of a
  const tag = React.createElement(
    `${
      attributes.link.type == "Dropdown" || attributes.link.type == "MegaMenu"
        ? "div"
        : "a"
    }`,
    {
      className: "nav-link",
      dangerouslySetInnerHTML: { __html: attributes.text },
      contentEditable: isEditing,
    }
  );

  // function generateMegaMenu() {
  //   if (attributes.link.type == "MegaMenu" && attributes.link.template_id) {
  //     const template = templates.find(
  //       (x) => x.id == attributes.link.template_id
  //     );

  //     if (template) {
  //       return (
  //         <MegaMenuComponent
  //           id={template.id}
  //           parentId={id}
  //           menuOpen={megaMenuOpen}
  //           setMegaMenuOpen={setMegaMenuOpen}
  //           leftPosition={attributes.link.left_position}
  //           topPosition={attributes.link.top_position}
  //           activateType={attributes.link.activate_type}
  //         />
  //       );
  //     }
  //   }
  // }

  // if (
  //   component.attributes.link.type === "MegaMenu" &&
  //   component.attributes.link.template_id
  // ) {

  //   if (component.childWebpageComponentRelations.length >= 1) {
  //     StructureAPI.addExistingComponent(pages.active!, {
  //       id: attributes.link.template_id!,
  //       parent_id: id ?? null,
  //       sort_order: 1,
  //     }).then(() => {
  //       StructureAPI.loadWebpageComponents(webpage_id!).then((x) => {
  //         setStructure(x);
  //       });
  //     });
  //   }
  // }

  return (
    <>
      <style>
        {`
      .nav-link p {
        margin-bottom: 0rem;
      }
    `}
      </style>

      <Li
        id={id.toString()}
        data-component-type="nav-item"
        className={
          "nav-item nav-link " +
          (classes ?? "") +
          (attributes && attributes.link && attributes.link.type === "Dropdown"
            ? " position-relative"
            : "") +
          " builder-component builder-component__nav-item" +
          (isEditing ? " builder-component--active" : "")
        }
        data-style={{
          ...styles,
          zIndex: isEditing ? 9 : "auto",
        }}
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          ...attributes.styles,
          position: "relative",
          pointerEvents: "visible",
        }}
        onMouseEnter={(e) => {
          if (isMobile) {
            return;
          }

          handleMegaMenuOpen(true);

          // setMegaMenuOpen(true);
        }}
        onMouseLeave={(e) => {
          if (isMobile) {
            return;
          }

          handleMegaMenuOpen(false);

          // setMegaMenuOpen(false);
        }}
        onClick={(e) => {
          if (isMobile) {
            return;
          }

          handleMegaMenuOpen(!megaMenuOpen);

          // setMegaMenuOpen(!megaMenuOpen);
        }}
      >
        <RenderChildren
          key={id + "children"}
          childWebpageComponentRelations={childWebpageComponentRelations.filter(
            (componentRelation) => {
              const childComponent = getComponentFromStructureMap(
                structure.components,
                getRelationChildKey(componentRelation) ?? componentRelation.child_id
              );
              if (childComponent && childComponent.type == "editor") {
                return true;
              }
              return false;
            }
          )}
        />

        {attributes &&
          attributes.link &&
          attributes.link.type !== "Dropdown" &&
          attributes.link.type !== "MegaMenu" &&
          !isEditing &&
          generateLink({
            pageUuid: attributes.link.pageUuid,
            pageId: attributes.link.pageId,
            type: attributes.link.type,
            url: attributes.link.url,
            target: attributes.link.target,
            text: attributes.text,
          }, !!(attributes as any).keepUrlParams)}

        {isBuilder && isEditing && editor && (
          <div>
            <EditorContent
              id={id + "_content"}
              content={attributes.text}
              editor={editor}
            ></EditorContent>
          </div>
        )}

        {attributes &&
          attributes.link &&
          attributes.link.type === "Dropdown" &&
          attributes.links &&
          !isEditing &&
          attributes.links.length >= 0 && <>{tag}</>}

        {attributes &&
          attributes.link &&
          attributes.link.type === "MegaMenu" &&
          !isEditing && (
            <span
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              {tag}
            </span>
          )}

        {attributes &&
          attributes.link &&
          attributes.link.type === "MegaMenu" &&
          attributes.link.template_id !== null &&
          !isEditing && (
            <>
              <MegaMenuComponent
                id={attributes.link.template_id}
                parentId={id}
                menuOpen={megaMenuOpen}
                setMegaMenuOpen={setMegaMenuOpen}
                leftPosition={attributes.link?.left_position}
                topPosition={attributes.link?.top_position}
                activateType={attributes.link?.activate_type}
              />
            </>
          )}

        {attributes &&
          attributes.link &&
          attributes.link.type === "Dropdown" &&
          attributes.links &&
          !isMobile &&
          attributes.links.length > 0 && (
            <>
              <div className="nav-subnav">
                {attributes.links && attributes.links.length > 0 && (
                  <>{attributes.links.map((item) => generateLink(item))}</>
                )}
              </div>
            </>
          )}

        {attributes &&
          attributes.link &&
          attributes.link.type === "Dropdown" &&
          attributes.links &&
          isMobile &&
          attributes.links.length > 0 && (
            <>
              <div
                style={{
                  width: "100%",
                  marginLeft: "15px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginTop: "15px",
                }}
              >
                {attributes.links.map((item) => generateLink(item))}
              </div>
            </>
          )}
      </Li>
    </>
  );
}
